<template>
  <div class="py-4 px-3">
    <div v-if="displayType === 'status'" class="flex items-center">
      <div
        v-if="sortedSequences.length > 0"
        :class="[
          'truncate flex flex-row items-center gap-1 text-[13px] ongoing_status ',
          (sortedSequences[0]?.status || '').toUpperCase() ===
            sequenceStatuses.ACTIVE ||
          sortedSequences[0]?.status === sequenceStatuses.IN_PROGRESS ||
          (sortedSequences[0]?.status || '').toUpperCase() ===
            sequenceStatuses.COMPLETE
            ? 'active_sequence_tag'
            : 'inactive_sequence_tag',
        ]"
      >
        <div
          :class="[
            'w-[6px] h-[6px] rounded-full',
            (sortedSequences[0]?.status || '').toUpperCase() ===
              sequenceStatuses.ACTIVE ||
            sortedSequences[0]?.status === sequenceStatuses.IN_PROGRESS ||
            (sortedSequences[0]?.status || '').toUpperCase() ===
              sequenceStatuses.COMPLETE
              ? 'bg-white'
              : 'status_dot_inactive',
          ]"
        ></div>
        <span
          :class="[
            (sortedSequences[0]?.status || '').toUpperCase() ===
              sequenceStatuses.ACTIVE ||
            sortedSequences[0]?.status === sequenceStatuses.IN_PROGRESS ||
            (sortedSequences[0]?.status || '').toUpperCase() ===
              sequenceStatuses.COMPLETE
              ? 'text-white'
              : 'text_color',
            'text-[11px] font-semibold',
          ]"
        >
          {{ getStatusDisplayText(sortedSequences[0]?.status) }}
        </span>
      </div>
      <span v-else class="text-sm secondary_color">No status</span>
    </div>

    <div
      v-else-if="displayType === 'sequences'"
      class="flex items-center w-full"
    >
      <div v-if="sortedSequences.length === 0" class="text-sm secondary_color">
        No {{ featureNames.SEQUENCES }}s
      </div>

      <div v-else-if="sortedSequences.length === 1" class="text-sm">
        {{ sortedSequences[0]?.name || `Unnamed ${featureNames.SEQUENCES}` }}
      </div>

      <div v-else class="flex items-center gap-2">
        <span class="text-sm">{{
          sortedSequences[0]?.name || `Unnamed ${featureNames.SEQUENCES}`
        }}</span>

        <a-dropdown
          trigger="hover"
          :mouseEnterDelay="0.2"
          :mouseLeaveDelay="0.2"
          placement="bottomLeft"
          :getPopupContainer="getPopupContainer"
        >
          <span class="selected_item_tag text-xs cursor-pointer">
            +{{ sortedSequences.length - 1 }}
          </span>
          <template #overlay>
            <a-menu
              class="sequence-dropdown-menu"
              style="max-height: 200px; overflow-y: auto"
            >
              <a-menu-item
                v-for="(seq, index) in sortedSequences.slice(1)"
                :key="seq.id || index"
                @click="selectSequence(seq)"
                class="sequence-menu-item"
              >
                <span class="text-sm">{{
                  seq.name || `Unnamed ${featureNames.SEQUENCES}`
                }}</span>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { sequenceStatuses } from "@/src/constants/sequenceConstants.js";
import { featureNames } from "@/src/constants/globalConstants";

const props = defineProps({
  sequences: {
    type: Array,
    default: () => [],
  },
  displayType: {
    type: String,
    required: true,
    validator: (value) => ["sequences", "status"].includes(value),
  },
  row: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["sequence-selected"]);

const selectedSequenceId = ref(null);

const getPopupContainer = (triggerNode) => {
  const modalContent = triggerNode.closest(".ant-modal-content");
  if (modalContent) {
    return modalContent;
  }

  const tableContainer =
    triggerNode.closest(".ant-table-tbody") ||
    triggerNode.closest(".ant-table-container");
  if (tableContainer) {
    return tableContainer;
  }

  return document.body;
};

const sortedSequences = computed(() => {
  if (!props.sequences || props.sequences.length === 0) {
    return [];
  }

  const sequences = [...props.sequences];

  if (selectedSequenceId.value) {
    const selectedIndex = sequences.findIndex(
      (seq) => (seq.id || seq.name) === selectedSequenceId.value,
    );

    if (selectedIndex > -1) {
      const selectedSeq = sequences.splice(selectedIndex, 1)[0];
      sequences.unshift(selectedSeq);
    }
  }

  return sequences;
});

const getStatusDisplayText = (status) => {
  if (!status) return "N/A";

  switch (status.toUpperCase()) {
    case "ACTIVE":
    case "IN_PROGRESS":
      return "Active";
    case "INACTIVE":
    case "IN_ACTIVE":
    case "PAUSED":
      return "Inactive";
    case "COMPLETE":
    case "COMPLETED":
      return "Completed";
    default:
      return (
        status.charAt(0).toUpperCase() +
        status.slice(1).toLowerCase().replace(/_/g, " ")
      );
  }
};

const selectSequence = (selectedSeq) => {
  selectedSequenceId.value = selectedSeq.id || selectedSeq.name;

  const updatedSequences = [...props.sequences];
  const originalIndex = updatedSequences.findIndex(
    (seq) => (seq.id || seq.name) === selectedSequenceId.value,
  );

  if (originalIndex > -1) {
    const movedSeq = updatedSequences.splice(originalIndex, 1)[0];
    updatedSequences.unshift(movedSeq);

    emit("sequence-selected", {
      ...props.row,
      sequence_data: updatedSequences,
    });
  }
};

watch(
  () => props.sequences,
  (newSequences, oldSequences) => {
    if (newSequences && newSequences.length > 0) {
      const firstSequenceId = newSequences[0]?.id || newSequences[0]?.name;
      if (selectedSequenceId.value !== firstSequenceId) {
        selectedSequenceId.value = firstSequenceId;
      }
    }
  },
  { immediate: true },
);
</script>

<style scoped>
:deep(.sequence-dropdown-menu) {
  background: var(--dropdown-bg) !important;
  border-radius: 8px !important;
  box-shadow: var(--dropdown-boxshadow) !important;
  border: 1px solid var(--white-opacity-8) !important;
  padding: 4px !important;
  min-width: 160px !important;
}

:deep(.sequence-menu-item) {
  color: var(--text-color) !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  margin: 2px 0 !important;
  transition: background-color 0.2s ease !important;
}

:deep(.sequence-menu-item:hover) {
  background-color: var(--white-opacity-8) !important;
}

:deep(.sequence-menu-item .ant-menu-title-content) {
  color: var(--text-color) !important;
}

.ongoing_status {
  padding: 4px 8px;
  border-radius: 24px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.active_sequence_tag {
  background: var(--active-tag) !important;
}

.inactive_sequence_tag {
  background: var(--white-opacity-8) !important;
}

.status_dot_inactive {
  background: var(--text-color) !important;
  opacity: 0.6;
}

.selected_item_tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  padding: 4px 8px;
  border-radius: 12px;
  background: var(--white-opacity-8);
  border: 1px solid var(--white-opacity-12);
  color: var(--text-color);
  font-size: 11px;
  font-weight: 500;
  transition: background-color 0.2s ease;
}

.selected_item_tag:hover {
  background: var(--white-opacity-12);
}
</style>
